import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, firestore } from '../firebase.js';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { ROUTES } from '../routes/routeConstants.js';
import { hasActiveSubscription, hasVIPAccess, normalizePurchaseType } from '../utils/entitlements.js';

// Enhanced Route Guard with role-based access and account setup verification
const RouteGuard = ({ 
  children, 
  requireAuth = false, 
  requireVIP = false, 
  requireSubscription = false,
  requireAccountSetup = false, // New prop for account setup verification
  suggestDogSetup = false, // New prop for suggesting dog setup
  fallbackComponent = null 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authState, setAuthState] = useState({
    isAuthenticated: null,
    user: null,
    isLoading: true,
    accountSetupComplete: false,
    hasDogs: false,
    showDogSetupSuggestion: false
  });

  // Function to check if user has dogs
  const checkUserDogs = async (user) => {
    try {
      const dogsRef = collection(firestore, 'users', user.uid, 'dogs');
      const dogsSnapshot = await getDocs(dogsRef);
      return !dogsSnapshot.empty;
    } catch (error) {
      console.error('RouteGuard: Error checking user dogs:', error);
      return false;
    }
  };

  // Function to check if user has dismissed dog setup suggestion
  const hasDismissedDogSetup = () => {
    return localStorage.getItem('dogSetupDismissed') === 'true';
  };

  // Function to dismiss dog setup suggestion
  const dismissDogSetup = () => {
    localStorage.setItem('dogSetupDismissed', 'true');
    setAuthState(prev => ({ ...prev, showDogSetupSuggestion: false }));
  };

  // Function to verify account setup completeness
  const verifyAccountSetup = async (user) => {
    try {
      console.log('RouteGuard: Verifying account setup for user:', user.uid);
      
      // Add a small delay to allow Firestore writes to complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Check if user document exists
      const userDocRef = doc(firestore, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        console.log('RouteGuard: ❌ User document not found');
        return false;
      }
      
      // Check if at least one dog exists
      const dogsRef = collection(firestore, 'users', user.uid, 'dogs');
      const dogsSnapshot = await getDocs(dogsRef);
      
      if (dogsSnapshot.empty) {
        console.log('RouteGuard: ❌ No dogs found');
        return false;
      }
      
      // Check if any dog has required fields (not just the first one)
      const requiredFields = ['dogName', 'dogBreed', 'dogAge', 'dogGender'];
      let hasCompleteDog = false;
      
      for (const dogDoc of dogsSnapshot.docs) {
        const dogData = dogDoc.data();
        const missingFields = requiredFields.filter(field => !dogData[field]);
        
        if (missingFields.length === 0) {
          console.log('RouteGuard: ✅ Found complete dog:', dogDoc.id);
          hasCompleteDog = true;
          break;
        } else {
          console.log('RouteGuard: ❌ Incomplete dog found:', dogDoc.id, 'Missing fields:', missingFields);
        }
      }
      
      if (!hasCompleteDog) {
        console.log('RouteGuard: ❌ No complete dogs found');
        return false;
      }
      
      console.log('RouteGuard: ✅ Account setup verified');
      return true;
    } catch (error) {
      console.error('RouteGuard: Error verifying account setup:', error);
      return false;
    }
  };

  const getUserAccess = async (user) => {
    const userDocRef = doc(firestore, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);
    const userData = userDoc.exists() ? userDoc.data() : {};
    const purchasesRef = collection(firestore, 'users', user.uid, 'purchases');
    const purchasesSnapshot = await getDocs(purchasesRef);
    const purchaseIds = purchasesSnapshot.docs.map((purchaseDoc) => normalizePurchaseType(purchaseDoc.id));

    return {
      hasSubscription: hasActiveSubscription(userData, purchaseIds),
      isVIP: hasVIPAccess(userData, purchaseIds),
    };
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('RouteGuard.jsx: === AUTH STATE CHANGED ===');
      console.log('RouteGuard.jsx: User:', user?.uid);
      console.log('RouteGuard.jsx: Is authenticated:', !!user);
      console.log('RouteGuard.jsx: Current pathname:', location.pathname);
      console.log('RouteGuard.jsx: Requirements:', { requireAuth, requireVIP, requireSubscription, requireAccountSetup, suggestDogSetup });
      
      let accountSetupComplete = false;
      let hasDogs = false;
      let showDogSetupSuggestion = false;
      
      if (user) {
        hasDogs = await checkUserDogs(user);
        
        if (requireAccountSetup) {
          // Try verification with retry mechanism
          let retries = 0;
          const maxRetries = 3;
          
          while (retries < maxRetries) {
            accountSetupComplete = await verifyAccountSetup(user);
            if (accountSetupComplete) {
              break;
            }
            retries++;
            if (retries < maxRetries) {
              console.log(`RouteGuard: Retry ${retries}/${maxRetries} for account setup verification`);
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        }
        
        // Check if we should show dog setup suggestion
        if (suggestDogSetup && !hasDogs && !hasDismissedDogSetup()) {
          showDogSetupSuggestion = true;
        }
      }
      
      setAuthState({
        isAuthenticated: !!user,
        user,
        isLoading: false,
        accountSetupComplete,
        hasDogs,
        showDogSetupSuggestion
      });

      // Handle authentication requirements
      if (requireAuth && !user) {
        console.log('RouteGuard.jsx: ⚠️ AUTHENTICATION REQUIRED BUT NO USER - REDIRECTING TO LOGIN');
        console.log('RouteGuard.jsx: Current pathname:', location.pathname);
        console.log('RouteGuard.jsx: Auth state:', { hasUser: !!user, uid: user?.uid });
        const redirectPath = `${location.pathname}${location.search}`;
        console.log('RouteGuard.jsx: Redirect path:', redirectPath);
        navigate(`${ROUTES.LOGIN}?redirect=${encodeURIComponent(redirectPath)}`, {
          state: { email: new URLSearchParams(location.search).get('email') }
        });
        return;
      }

      // Handle account setup requirements
      if (requireAccountSetup && user && !accountSetupComplete) {
        console.log('RouteGuard.jsx: ⚠️ ACCOUNT SETUP REQUIRED BUT INCOMPLETE - REDIRECTING TO ADD DOG');
        navigate(ROUTES.ADD_DOG, {
          state: { 
            incompleteSetup: true,
            redirectAfterSetup: location.pathname
          }
        });
        return;
      }

      // Handle VIP requirements (you'll need to implement VIP check logic)
      if (requireVIP && user) {
        const { isVIP } = await getUserAccess(user);
        if (!isVIP) {
          navigate(ROUTES.MEMBERSHIP);
          return;
        }
      }

      // Handle subscription requirements (you'll need to implement subscription check logic)
      if (requireSubscription && user) {
        const { hasSubscription } = await getUserAccess(user);
        if (!hasSubscription) {
          navigate(ROUTES.SUBSCRIPTION);
          return;
        }
      }
    });

    return () => unsubscribe();
  }, [navigate, location, requireAuth, requireVIP, requireSubscription, requireAccountSetup, suggestDogSetup]);

  // Loading state
  if (authState.isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Show fallback component if provided and requirements not met
  if (fallbackComponent && !authState.isAuthenticated && requireAuth) {
    return fallbackComponent;
  }

  // Render children
  return <>{children}</>;
};

// Convenience components for different access levels
export const PrivateRoute = ({ children, ...props }) => (
  <RouteGuard requireAuth={true} {...props}>
    {children}
  </RouteGuard>
);

export const AccountSetupRoute = ({ children, ...props }) => (
  <RouteGuard requireAuth={true} requireAccountSetup={true} {...props}>
    {children}
  </RouteGuard>
);

export const VIPRoute = ({ children, ...props }) => (
  <RouteGuard requireAuth={true} requireVIP={true} {...props}>
    {children}
  </RouteGuard>
);

export const SubscriptionRoute = ({ children, ...props }) => (
  <RouteGuard requireAuth={true} requireSubscription={true} {...props}>
    {children}
  </RouteGuard>
);

export default RouteGuard; 
